import React, { useState } from 'react';

import '../myAccount.scss';
import Button from '../../Form/Button/Button';
import ButtonGroup from '../../Form/Button/ButtonGroup';
import { formatDate, formatCurrency } from '../../../utils/helpers';
import Modal from '../../Modal/Modal';
import Loading from '../../Loading/Loading';
import { gql } from 'apollo-boost';
import { useApolloClient } from '@apollo/react-hooks';

/** @import {NsAccountCancelCloseProps} from './NsAccountCancelCloseTypes' */
/** @import {PendingTransaction} from '../../../contexts/CustomerDataProviderTypes' */

import successImg from '../../../assets/images/success.png';
import errorImg from '../../../assets/images/error.png';

function NsAccountCancelClose(/** @type {NsAccountCancelCloseProps} */ props) {
    const profileData = props.profileData;
    const apolloClient = useApolloClient();
    const [showCancelNoticeModel, setShowCancelNoticeModel] = useState(false);
    const [showProcessModel, setShowProcessModel] = useState(false);
    const [showSuccessModel, setShowSuccessModel] = useState(false);
    const [showFailedModel, setShowFailedModel] = useState(false);

    const resetAllModals = () => {
        setShowCancelNoticeModel(false);
        setShowProcessModel(false);
        setShowSuccessModel(false);
        setShowFailedModel(false);
    };

    const resetOnSuccess = () => {
        resetAllModals();
        profileData.pendingTransaction.state = 'cancellationrequested';
    };

    const onCancelNoticeModalOpen = () => {
        resetAllModals();
        setShowCancelNoticeModel(true);
    };

    const onSuccessModalOpen = () => {
        resetAllModals();
        setShowSuccessModel(true);
    };

    const onFailedModalOpen = () => {
        resetAllModals();
        setShowFailedModel(true);
    };

    const renderProcessingModal = () => {
        return (
            <Modal>
                <Loading message="Processing..." />
            </Modal>
        );
    };

    const renderSuccessModal = () => {
        return (
            <Modal onClose={resetOnSuccess}>
                <img src={successImg} className='account-activity__image' />
                <h4 className="account-activity__modal-title">Done!</h4>
                <Button size='med' customClassNames={["modal__button"]} label="OK" onClick={resetOnSuccess}/>            
            </Modal>
        );
    };

    const renderFailedModal = () => {
        return (
            <Modal onClose={resetAllModals}>
                <img src={errorImg} className='account-activity__image' />
                <h4 className="account-activity__title">Failed!</h4>
                <p className="account-activity__slim">Please try again later.</p>
            </Modal>
        );
    }

    const renderCancelNoticeAccountClosureModal = () => {
        return (
	     	     <Modal className="modal modal--fixed-height modal--wide modal__mobile-optimised" onClose={resetAllModals}>
			       <h4 className="modal__title">Do you want to cancel this withdrawal and keep your account open?</h4>
                   <div className="account-activity__grouping">
                     <div className="account-activity__grouping-item">
                        <p className="account-activity__slim-title">Scheduled withdrawal date</p>
                        <p className="account-activity__slim-value">{formatDate(new Date(profileData.pendingTransaction.withdrawalDate))}</p>
                     </div>
                     <div className="account-activity__grouping-item">
                        <p className="account-activity__slim-title">Amount</p>
                        <p className="account-activity__slim-value">{profileData.pendingTransaction.amount ? formatCurrency(Math.abs(profileData.pendingTransaction.amount)) : '£0'}</p>
                     </div>
                   </div>
                   <p className="modal__slim">If you cancel this withdrawal, we won't send this money to your chosen account on the date above and this account will stay open.</p>
                   <p className="modal__slim">Don't forget that if you schedule another withdrawal in the future, you'll need to wait your full 90-day notice period again.</p>
                   <div className="modal__action-group">
                     <ButtonGroup>
                       <Button size='med'
                            label='Yes, cancel my withdrawal'
                            onClick={() => sendCancelNoticeAccountClosure(profileData.accountId, profileData.pendingTransaction)}
                       />
                       <Button variant='secondary'
                            size='med'
                            label='No, do not cancel it'
                            onClick={resetAllModals}
                       />
                     </ButtonGroup>
                   </div>
                 </Modal>
		       );
      };

    const sendCancelNoticeAccountClosure = (/** @type {string} */ accountId, /** @type {PendingTransaction} */ pendingTransaction) => {
        resetAllModals();
        setShowProcessModel(true);

        apolloClient.mutate({
            mutation: gql`
                mutation CancelNoticeWithdrawalRequest($request : CancelNoticeWithdrawalRequestType!){
                    cancelNoticeWithdrawal(request:$request) {
                      success
                    }
                } 
            `,
            variables: {
                request: {
                    accountId: accountId,
                    pendingTransactionId: pendingTransaction.pendingTransactionId
                }
            }
        }).then(result => {
            if (result.data.cancelNoticeWithdrawal.success) {
                onSuccessModalOpen();
            } else {
                onFailedModalOpen();
            }
        }).catch(error => {
            console.log('error::' + error);
        });
    }

    const renderRequestPending = () => {
        return (
            <>
                <div className="my-account__content my-account__content--medium-margin-bottom">
                    <h1 className="my-account__secondary-title">Cancel closure request has been submitted</h1>
                </div>
            </>
        );
    }

    if (profileData.pendingTransaction.state === 'cancellationrequested')
        return renderRequestPending();

    const calculateCancelDate = (/** @type {string} */ withdrawalDate, /** @type {number} */ days) => {
        let date = new Date(withdrawalDate);
        date.setDate(date.getDate() - days);
        return formatDate(date);
    }

    return (
        <>
            <div className="my-account__content my-account__full-width-content">
                <h1 className="my-account__secondary-title">This account closes on {formatDate(new Date(profileData.pendingTransaction.withdrawalDate))}</h1>
                <p className="my-account__text">On this date, your full balance and any interest earned until then will be paid into your {profileData.pendingTransaction.counterpartAccountType} ({profileData.pendingTransaction.counterpartAccountNumber}).</p>
            </div>
            <div className="my-account__content my-account__full-width-content my-account__content--small-margin-bottom">
                {profileData.pendingTransaction && profileData.pendingTransaction.allowClosure && (<div className="my-account__row">
                    <div className="my-account__col my-account__col--full-width button-mb">
                        <Button size='med'
                            label={"Don't close my account"}
                            onClick={onCancelNoticeModalOpen}
                            disabled={showCancelNoticeModel || showProcessModel || showSuccessModel || showFailedModel}
                        />
                    </div>
                    <div className="my-account__col my-account__doubleCol my-account__col--full-width my-account__col--small-margin-left my-account__nstext">
                        You can change your mind about what you'd like to do with your Notice Saver until {calculateCancelDate(profileData.pendingTransaction.withdrawalDate, 1)} at 20:00.
                    </div>
                </div>)}
            </div>

            {showCancelNoticeModel && renderCancelNoticeAccountClosureModal()}

            {showProcessModel && renderProcessingModal()}

            {showSuccessModel && renderSuccessModal()}

            {showFailedModel && renderFailedModal()}
        </>
    )
};

export default NsAccountCancelClose;
